import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, X, Settings, Check } from 'lucide-react';

function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be changed
    functional: true,
    analytics: false
  });

  useEffect(() => {
    // Check if consent already exists
    try {
      const consent = localStorage.getItem('cookieConsent');
      if (!consent) {
        // Show banner after small delay for better UX
        const timer = setTimeout(() => {
          setShowBanner(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      // If localStorage is not available, show banner anyway
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const acceptSelected = () => {
    const selected = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(selected));
    setShowBanner(false);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg animate-in slide-in-from-bottom duration-300"
    >
      <div className="max-w-7xl mx-auto">
        {!showSettings ? (
          /* Main Banner */
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 bg-green-100 rounded-lg shrink-0">
                <Cookie className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Používame cookies</h3>
                <p className="text-sm text-slate-600">
                  Táto stránka používa cookies na zlepšenie vášho zážitku. Nevyhnutné cookies sú potrebné pre fungovanie webu. 
                  Analytické cookies nám pomáhajú zlepšovať naše služby.{' '}
                  <Link to="/ochrana-osobnych-udajov" className="text-green-600 hover:underline">
                    Viac informácií
                  </Link>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              <Button
                data-testid="cookie-settings-btn"
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="text-slate-600 border-slate-300"
              >
                <Settings className="h-4 w-4 mr-1" />
                Nastavenia
              </Button>
              <Button
                data-testid="cookie-reject-btn"
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="text-slate-600 border-slate-300"
              >
                Len nevyhnutné
              </Button>
              <Button
                data-testid="cookie-accept-btn"
                size="sm"
                onClick={acceptAll}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Check className="h-4 w-4 mr-1" />
                Prijať všetky
              </Button>
            </div>
          </div>
        ) : (
          /* Settings Panel */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Settings className="h-5 w-5 text-green-600" />
                Nastavenia cookies
              </h3>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            
            <div className="grid gap-3 sm:grid-cols-3">
              {/* Necessary */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900 text-sm">Nevyhnutné</span>
                  <div className="w-10 h-5 bg-green-600 rounded-full flex items-center justify-end px-0.5">
                    <div className="w-4 h-4 bg-white rounded-full shadow" />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Potrebné pre základné fungovanie webu (prihlásenie, bezpečnosť).
                </p>
              </div>
              
              {/* Functional */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900 text-sm">Funkčné</span>
                  <button
                    data-testid="cookie-functional-toggle"
                    onClick={() => setPreferences(p => ({ ...p, functional: !p.functional }))}
                    className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                      preferences.functional ? 'bg-green-600 justify-end' : 'bg-slate-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow" />
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Zapamätanie vašich preferencií a nastavení.
                </p>
              </div>
              
              {/* Analytics */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900 text-sm">Analytické</span>
                  <button
                    data-testid="cookie-analytics-toggle"
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                      preferences.analytics ? 'bg-green-600 justify-end' : 'bg-slate-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full shadow" />
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Anonymná analýza návštevnosti pre zlepšenie služieb.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(false)}
                className="text-slate-600"
              >
                Späť
              </Button>
              <Button
                data-testid="cookie-save-btn"
                size="sm"
                onClick={acceptSelected}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Uložiť nastavenia
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookieBanner;
